/** @format */

import React from "react";
import {
	Box,
	Container,
	Typography,
	Grid,
	Card,
	CardContent,
	Button,
	styled,
	Paper,
} from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import SavingsIcon from "@mui/icons-material/Savings";
import SecurityIcon from "@mui/icons-material/Security";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import {
	ArrowForward,
	PersonAdd,
	AttachMoney,
	Autorenew,
	CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { colors } from "../../utility/types/constant";
import { CTAButtonsWrapper, PrimaryCTAButton } from "../banner-one/styles";
import { SEO } from "../allComponents/AllComponents";

const HeroSection = styled(Box)(({ theme }) => ({
	background: colors.primary,
	color: "#fff",
	padding: "80px 0",
	textAlign: "center",
	fontFamily: "'Montserrat', sans-serif",
}));

const FeatureCard = styled(Card)(({ theme }) => ({
	height: "100%",
	display: "flex",
	flexDirection: "column",
	transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
	"&:hover": {
		transform: "translateY(-8px)",
		boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
	},
}));

const IconWrapper = styled(Box)(({ theme }) => ({
	width: "64px",
	height: "64px",
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 16px",
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepCard = styled(Paper)(({ theme }) => ({
	backgroundColor: "#ffffff",
	borderRadius: "20px",
	padding: "40px 30px",
	boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
	border: "2px solid rgba(0,0,0,0.05)",
	transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
	textAlign: "center",
	position: "relative",
	overflow: "hidden",
	height: "100%",
	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		height: "4px",
		background: colors.mainSecondary,
		transform: "scaleX(0)",
		transition: "transform 0.4s ease",
	},
	"&:hover": {
		transform: "translateY(-10px)",
		boxShadow: "0 20px 40px rgba(30, 193, 149, 0.2)",
		borderColor: colors.mainSecondary,
		"&::before": {
			transform: "scaleX(1)",
		},
	},
	[theme.breakpoints.down("md")]: {
		padding: "30px 20px",
	},
}));

const StepNumber = styled(Typography)(({ theme }) => ({
	position: "absolute",
	top: "10px",
	right: "15px",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(48px, 6vw, 72px)",
	fontWeight: 900,
	color: "rgba(30, 193, 149, 0.08)",
	lineHeight: 1,
}));

const StepIconWrapper = styled(Box)(({ theme }) => ({
	width: "80px",
	height: "80px",
	borderRadius: "50%",
	backgroundColor: colors.mainSecondary,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 24px auto",
	color: "#ffffff",
	transition: "all 0.3s ease",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
	fontWeight: 700,
	color: colors.primary,
	marginBottom: "12px",
}));

const StepDescription = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "0.9375rem",
	fontWeight: 400,
	color: "#6c757d",
	lineHeight: 1.7,
}));

const ConnectorLine = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	[theme.breakpoints.down("md")]: {
		display: "none",
	},
}));

const AnimatedArrow = styled(Box)(({ theme }) => ({
	color: colors.mainSecondary,
	animation: "slideRight 2s ease-in-out infinite",
	"@keyframes slideRight": {
		"0%, 100%": {
			transform: "translateX(0)",
		},
		"50%": {
			transform: "translateX(10px)",
		},
	},
}));

const CTAButton = styled(Button)(({ theme }) => ({
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	padding: "12px 32px",
	fontSize: "16px",
	fontWeight: 600,
	borderRadius: "8px",
	textTransform: "none",
	"&:hover": {
		backgroundColor: colors.mainSecondary,
		transform: "scale(1.05)",
	},
}));

const SavingsCircle: React.FC = () => {
	const navigate = useNavigate();
	const handleJoinClick = () => {
		navigate("/circle/sign-up");
	};

	const features = [
		{
			icon: <GroupsIcon sx={{ fontSize: 40 }} />,
			title: "Community-Powered Savings",
			description:
				"Join a trusted circle of friends, family, or colleagues to save together and achieve your financial goals faster.",
		},
		{
			icon: <SavingsIcon sx={{ fontSize: 40 }} />,
			title: "Flexible Contributions",
			description:
				"Choose contribution amounts and frequencies that work for your budget. Start small and scale as you grow.",
		},
		{
			icon: <SecurityIcon sx={{ fontSize: 40 }} />,
			title: "Secure & Transparent",
			description:
				"Bank-level security with full transparency. Track every contribution and payout in real-time.",
		},
		{
			icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
			title: "Accelerated Savings",
			description:
				"Receive lump sum payouts that help you make bigger purchases or investments sooner than saving alone.",
		},
		{
			icon: <CalendarTodayIcon sx={{ fontSize: 40 }} />,
			title: "Automated Scheduling",
			description:
				"Set it and forget it. Automatic reminders and scheduled contributions keep everyone on track.",
		},
		{
			icon: <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />,
			title: "No Hidden Fees",
			description:
				"Transparent pricing with no surprise charges. What you save is what you get.",
		},
	];

	const howItWorksSteps = [
		{
			step: "01",
			icon: <PersonAdd sx={{ fontSize: 40 }} />,
			title: "Join a Circle",
			description:
				"Browse available circles or create your own. Choose the contribution amount and frequency that fits your budget.",
		},
		{
			step: "02",
			icon: <AttachMoney sx={{ fontSize: 40 }} />,
			title: "Make Contributions",
			description:
				"Contribute your agreed amount regularly. All members pool their money together each cycle.",
		},
		{
			step: "03",
			icon: <Autorenew sx={{ fontSize: 40 }} />,
			title: "Receive Your Payout",
			description:
				"When it's your turn in the rotation, receive the full pot. Use it for your goals, debt, savings, or investments.",
		},
		{
			step: "04",
			icon: <CheckCircle sx={{ fontSize: 40 }} />,
			title: "Complete the Cycle",
			description:
				"Continue contributing until everyone receives their payout. Build trust and financial stability together.",
		},
	];

	const benefits = [
		"Build disciplined saving habits with accountability",
		"Access larger sums of money without loans or interest",
		"Strengthen relationships through shared financial goals",
		"Achieve major purchases faster (down payments, education, emergencies)",
		"Learn financial management in a supportive community",
		"No credit checks or complex approval processes",
	];

	return (
		<>
			<SEO
				title="Savings Circles"
				description="Join community-powered savings circles with FundsyFi. Save together with friends, family, or colleagues through flexible contributions and secure, transparent payouts."
				keywords="savings circles, ROSCA, susu, tontine, rotating savings, community savings, group savings account, money pool"
				url="/products/savings-circles"
			/>
			<Box sx={{ bgcolor: "#f5f5f5" }}>
				{/* Hero Section */}
				<HeroSection>
					<Container maxWidth="lg">
						<Typography
							variant="h2"
							sx={{
								fontWeight: 700,
								mb: 2,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Savings Circles
						</Typography>
						<Typography
							variant="h5"
							sx={{
								mb: 4,
								opacity: 0.9,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Save Together, Achieve More
						</Typography>
						<Typography
							variant="h6"
							sx={{
								maxWidth: "800px",
								margin: "0 auto",
								mb: 4,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Transform the way you save with community-powered savings circles.
							Pool resources with trusted groups, contribute regularly, and
							receive lump sum payouts to achieve your financial goals faster.
						</Typography>
						<CTAButtonsWrapper
							data-aos="fade-up"
							data-aos-duration="1000"
							data-aos-delay="400">
							<PrimaryCTAButton
								variant="contained"
								size="large"
								onClick={handleJoinClick}>
								Get Started Free
							</PrimaryCTAButton>
						</CTAButtonsWrapper>
					</Container>
				</HeroSection>

				{/* What is a Savings Circle */}
				<Container maxWidth="lg" sx={{ py: 8 }}>
					<Typography
						variant="h3"
						align="center"
						sx={{
							fontWeight: 800,
							mb: 2,
							color: colors.primary,
							fontFamily: "'Montserrat', sans-serif",
							fontSize: "clamp(2rem, 4vw, 3rem)",
							letterSpacing: "-0.02em",
						}}>
						What is a Savings Circle?
					</Typography>
					<Typography
						variant="body1"
						align="center"
						sx={{
							maxWidth: "900px",
							margin: "0 auto 6rem",
							fontSize: "clamp(1rem, 2vw, 1.25rem)",
							color: "#6c757d",
							fontFamily: "'Montserrat', sans-serif",
							lineHeight: 1.7,
						}}>
						A Savings Circle, also known as a ROSCA (Rotating Savings and Credit
						Association), Tanda, Chit Fund, SuSu, etc..., is a time vested
						method where a group of people pool their money together at regular
						intervals. Each cycle, one member receives the entire pot, rotating
						until everyone has had their turn. It's a powerful way to save,
						build trust, and achieve financial milestones together.
					</Typography>

					{/* Features Grid */}
					<Grid container spacing={4} sx={{ mb: 8 }}>
						{features.map((feature, index) => (
							<Grid item xs={12} sm={6} md={4} key={index}>
								<FeatureCard>
									<CardContent>
										<IconWrapper>{feature.icon}</IconWrapper>
										<Typography
											variant="h6"
											sx={{ fontWeight: 600, mb: 2, color: "#333" }}>
											{feature.title}
										</Typography>
										<Typography
											variant="body2"
											sx={{ color: "#666", lineHeight: 1.7 }}>
											{feature.description}
										</Typography>
									</CardContent>
								</FeatureCard>
							</Grid>
						))}
					</Grid>
				</Container>

				{/* How It Works */}
				<Box sx={{ bgcolor: "#fff", py: 8 }}>
					<Container maxWidth="lg">
						<Typography
							variant="h3"
							align="center"
							sx={{
								fontWeight: 800,
								mb: 2,
								color: colors.primary,
								fontFamily: "'Montserrat', sans-serif",
								fontSize: "clamp(2rem, 4vw, 3rem)",
								letterSpacing: "-0.02em",
							}}>
							How It Works
						</Typography>
						<Typography
							variant="body1"
							align="center"
							sx={{
								maxWidth: "800px",
								margin: "0 auto 6rem",
								fontSize: "clamp(1rem, 2vw, 1.25rem)",
								color: "#6c757d",
								fontFamily: "'Montserrat', sans-serif",
								lineHeight: 1.7,
							}}>
							Join a rotating savings circle in 4 simple steps and start
							building your financial future with your community
						</Typography>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: {
									xs: "1fr",
									md: "1fr auto 1fr auto 1fr auto 1fr",
								},
								gap: { xs: "40px", md: "20px" },
								alignItems: "center",
							}}>
							{howItWorksSteps.map((item, index) => (
								<React.Fragment key={index}>
									<StepCard elevation={2}>
										<StepNumber>{item.step}</StepNumber>
										<StepIconWrapper>{item.icon}</StepIconWrapper>
										<StepTitle>{item.title}</StepTitle>
										<StepDescription>{item.description}</StepDescription>
									</StepCard>

									{index < howItWorksSteps.length - 1 && (
										<ConnectorLine>
											<AnimatedArrow>
												<ArrowForward sx={{ fontSize: 32 }} />
											</AnimatedArrow>
										</ConnectorLine>
									)}
								</React.Fragment>
							))}
						</Box>
					</Container>
				</Box>

				{/* Benefits Section */}
				<Container maxWidth="lg" sx={{ py: 8 }}>
					<Typography
						variant="h3"
						align="center"
						sx={{
							fontWeight: 800,
							mb: 2,
							color: colors.primary,
							fontFamily: "'Montserrat', sans-serif",
							fontSize: "clamp(2rem, 4vw, 3rem)",
							letterSpacing: "-0.02em",
						}}>
						Why Choose Savings Circles?
					</Typography>
					<Typography
						variant="body1"
						align="center"
						sx={{
							maxWidth: "800px",
							margin: "0 auto 6rem",
							fontSize: "clamp(1rem, 2vw, 1.25rem)",
							color: "#6c757d",
							fontFamily: "'Montserrat', sans-serif",
							lineHeight: 1.7,
						}}>
						Experience the power of collective savings with benefits that go
						beyond just money
					</Typography>

					<Grid
						container
						spacing={3}
						sx={{ maxWidth: "900px", margin: "0 auto" }}>
						{benefits.map((benefit, index) => (
							<Grid item xs={12} sm={6} key={index}>
								<Box sx={{ display: "flex", alignItems: "flex-start" }}>
									<Box
										sx={{
											width: "24px",
											height: "24px",
											borderRadius: "50%",
											backgroundColor: colors.mainSecondary,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: "#fff",
											fontSize: "14px",
											fontWeight: 600,
											mr: 2,
											flexShrink: 0,
											mt: 0.5,
										}}>
										✓
									</Box>
									<Typography
										variant="body1"
										sx={{
											color: "#6c757d",
											lineHeight: 1.7,
											fontFamily: "'Montserrat', sans-serif",
										}}>
										{benefit}
									</Typography>
								</Box>
							</Grid>
						))}
					</Grid>
				</Container>

				{/* CTA Section */}
				<Box
					sx={{
						backgroundColor: colors.primary,
						color: "#fff",
						py: 8,
					}}>
					<Container maxWidth="md" sx={{ textAlign: "center" }}>
						<Typography
							variant="h3"
							sx={{
								fontWeight: 700,
								mb: 2,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Ready to Start Saving Smarter?
						</Typography>
						<Typography
							variant="h6"
							sx={{
								mb: 4,
								opacity: 0.9,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Join thousands of people already achieving their financial goals
							through Savings Circles
						</Typography>
						<Box
							sx={{
								display: "flex",
								gap: 2,
								justifyContent: "center",
								flexWrap: "wrap",
							}}>
							<CTAButton
								onClick={() => navigate("/circle/sign-up")}
								sx={{
									bgcolor: "#fff",
									color: colors.mainPrimary,
									"&:hover": {
										bgcolor: "#fff",
									},
								}}>
								Create Your Circle
							</CTAButton>
							<CTAButton
								onClick={() => navigate("/circle/sign-in")}
								sx={{
									bgcolor: "transparent",
									border: "2px solid #fff",
									"&:hover": {
										bgcolor: "rgba(255,255,255,0.1)",
									},
								}}>
								Sign In
							</CTAButton>
						</Box>
					</Container>
				</Box>
			</Box>
		</>
	);
};

export default SavingsCircle;
